import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, UserPlus, Trash2, Mail, Shield, Eye, Crown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsuariosAcademiaModule({ academiaId, adminUserId, currentUserId }) {
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [showInvitarDialog, setShowInvitarDialog] = useState(false);
  const [invitarForm, setInvitarForm] = useState({ email: '', rol: 'lectura' });
  const [enviando, setEnviando] = useState(false);

  const isAdmin = currentUserId === adminUserId;

  useEffect(() => {
    if (academiaId) loadUsuarios();
  }, [academiaId]);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academias/${academiaId}/usuarios`, { withCredentials: true });
      setUsuarios(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvitar = async () => {
    if (!invitarForm.email.trim()) {
      toast.error('El email es requerido');
      return;
    }
    
    setEnviando(true);
    try {
      const res = await axios.post(`${API}/academias/${academiaId}/usuarios/invitar`, invitarForm, { withCredentials: true });
      toast.success(res.data.message);
      setShowInvitarDialog(false);
      setInvitarForm({ email: '', rol: 'lectura' });
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar usuario');
    } finally {
      setEnviando(false);
    }
  };

  const handleCambiarRol = async (usuario, nuevoRol) => {
    try {
      await axios.put(`${API}/academias/${academiaId}/usuarios/${usuario.id}`, {
        role: nuevoRol
      }, { withCredentials: true });
      toast.success('Rol actualizado');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  const handleEliminar = async (usuario) => {
    if (!window.confirm(`¿Remover acceso de ${usuario.email} a la academia?`)) return;
    
    try {
      await axios.delete(`${API}/academias/${academiaId}/usuarios/${usuario.id}`, { withCredentials: true });
      toast.success('Usuario removido de la academia');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al remover usuario');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuarios</h1>
          <p className="text-slate-400 text-sm">Gestiona el acceso a la academia</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowInvitarDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <UserPlus className="w-4 h-4 mr-2" /> Invitar Usuario
          </Button>
        )}
      </div>

      {/* Lista de usuarios */}
      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Usuario</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Rol</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {usuarios.map(usuario => {
              const rol = usuario.role || usuario.rol;
              const esAdmin = usuario.es_admin || rol === 'admin';
              const esMismo = usuario.user_id === currentUserId;
              const puedeRemover = !esAdmin && !esMismo && usuario.puede_eliminar !== false;
              const puedeEditarRol = !esAdmin && !esMismo && isAdmin;
              
              return (
                <tr key={usuario.id} className="hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${esAdmin ? 'bg-amber-500/20' : 'bg-slate-700'}`}>
                        {esAdmin ? <Crown className="w-5 h-5 text-amber-400" /> : <Users className="w-5 h-5 text-slate-400" />}
                      </div>
                      <div>
                        <p className="font-medium text-white">{usuario.nombre || usuario.user_info?.name || usuario.email}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {usuario.email}
                        </p>
                        {usuario.es_invitacion_pendiente && (
                          <Badge className="mt-1 bg-yellow-600/20 text-yellow-400 text-xs">Pendiente de aceptar</Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {esAdmin ? (
                      <Badge className="bg-amber-500/20 text-amber-400 flex items-center gap-1 w-fit">
                        <Crown className="w-3 h-3" /> Administrador
                      </Badge>
                    ) : puedeEditarRol ? (
                      <Select 
                        value={rol} 
                        onValueChange={(nuevoRol) => handleCambiarRol(usuario, nuevoRol)}
                      >
                        <SelectTrigger className="w-36 h-8 bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="super_usuario">
                            <span className="flex items-center gap-2">
                              <Shield className="w-3 h-3 text-purple-400" /> Super Usuario
                            </span>
                          </SelectItem>
                          <SelectItem value="lectura">
                            <span className="flex items-center gap-2">
                              <Eye className="w-3 h-3 text-blue-400" /> Solo Lectura
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`flex items-center gap-1 w-fit ${rol === 'super_usuario' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {rol === 'super_usuario' ? <><Shield className="w-3 h-3" /> Super Usuario</> : <><Eye className="w-3 h-3" /> Solo Lectura</>}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {puedeRemover && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                        onClick={() => handleEliminar(usuario)}
                      >
                        Remover
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Info de roles */}
      <Card className="p-4 bg-slate-800/50 border-slate-700">
        <h3 className="text-sm font-medium text-white mb-3">Roles disponibles</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-purple-400 mt-0.5" />
            <div>
              <p className="text-purple-400 font-medium">Super Usuario</p>
              <p className="text-slate-400">Puede crear, editar y eliminar información. No puede gestionar usuarios ni configuración.</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Eye className="w-4 h-4 text-blue-400 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium">Solo Lectura</p>
              <p className="text-slate-400">Solo puede ver información. No puede hacer cambios.</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Dialog Invitar */}
      <Dialog open={showInvitarDialog} onOpenChange={setShowInvitarDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Invitar Usuario
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Email del usuario *</Label>
              <Input
                type="email"
                value={invitarForm.email}
                onChange={(e) => setInvitarForm({ ...invitarForm, email: e.target.value })}
                placeholder="usuario@email.com"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Si el usuario ya tiene cuenta en Kinosis, se agregará automáticamente. Si no, recibirá una invitación por email.
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Rol</Label>
              <Select value={invitarForm.rol} onValueChange={(v) => setInvitarForm({ ...invitarForm, rol: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_usuario">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      Super Usuario
                    </span>
                  </SelectItem>
                  <SelectItem value="lectura">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-400" />
                      Solo Lectura
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowInvitarDialog(false)} className="flex-1 border-slate-600">
                Cancelar
              </Button>
              <Button onClick={handleInvitar} disabled={enviando} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {enviando ? 'Enviando...' : 'Enviar Invitación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
